// "Who am I" (DESIGN §2 + §8): Sidebar person area + the TopBar avatar menu.
// Signed in (server mode): the user card — avatar, name, role; menu Settings · Sign out. Local mode (no sign-in):
// the name switcher — choosing a team member calls setCurrentPerson(id) (localStorage 'frazier-person').
import './person-switcher.css';
import { useState } from 'preact/hooks';
import { MODE, accountOf, logout, setCurrentPerson, toggleTheme, useStore } from '../store.js';
import { href } from '../router.js';
import { Icon } from '../icons.jsx';
import { MenuList, Popover, usePopover } from './popover.jsx';
import { Avatar, cx } from './ui.jsx';

// Team members as menu items (alphabetical), the current one checked. onPicked runs after the choice.
export function personItems(team = [], currentId, onPicked) {
  return [...team]
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((m) => ({
      label: m.name,
      meta: m.role || undefined,
      avatar: <Avatar name={m.name} size={22} />,
      checked: m.id === currentId,
      onSelect: () => { setCurrentPerson(m.id); onPicked?.(m); },
    }));
}

const signOutItem = { label: 'Sign out', icon: 'logout', onSelect: () => { logout().catch(() => {}); } };

// Signed-in user card (sidebar): avatar, name, role ▾ (rail: avatar only) → Settings · Sign out.
function UserCard({ me, class: cls }) {
  const pop = usePopover();
  const role = me.role || 'Team member';
  const items = [{ label: 'Settings', icon: 'settings', href: href('/settings') }, { divider: true }, signOutItem];
  return (
    <>
      <button ref={pop.anchorRef} type="button" class={cx('person-switch user-card', cls)} title={`${me.name} · ${role}`} aria-label={`${me.name}, ${role} — account menu`} {...pop.triggerProps}>
        <Avatar name={me.name} size={28} />
        <span class="user-card-who nav-label">
          <span class="user-card-name">{me.name}</span>
          <span class="user-card-role">{role}</span>
        </span>
        <Icon name="chevronsUpDown" size={14} class="person-switch-caret nav-label" />
      </button>
      <Popover open={pop.open} onClose={pop.close} anchorRef={pop.anchorRef} placement="bottom-start" width={216} label="Account" role="menu" class="popover-menu">
        {/* The 64px rail shows only the avatar: the menu says who is signed in. */}
        <div class="avatar-menu-head user-card-head">
          <Avatar name={me.name} size={32} />
          <span class="avatar-menu-who">
            <span class="avatar-menu-name">{me.name}</span>
            <span class="avatar-menu-role">{role}</span>
          </span>
        </div>
        <MenuList items={items} onClose={pop.close} />
      </Popover>
    </>
  );
}

// Sidebar person area: the signed-in user card, else Avatar + name ▾ switcher. Empty state: "Choose your name".
export function PersonSwitcher({ class: cls }) {
  const snap = useStore();
  const { state, index, currentPerson } = snap;
  const account = accountOf(snap);
  if (account) return <UserCard me={account} class={cls} />;
  return <NameSwitcher state={state} index={index} currentPerson={currentPerson} class={cls} />;
}

function NameSwitcher({ state, index, currentPerson, class: cls }) {
  const pop = usePopover();
  const team = state?.team || [];
  const me = currentPerson ? index?.teamById?.get(currentPerson) : null;
  const items = team.length
    ? personItems(team, currentPerson)
    : [{ label: 'Add team members in People', href: href('/people'), icon: 'people' }];
  return (
    <>
      <button ref={pop.anchorRef} type="button" class={cx('person-switch', !me && 'is-empty', cls)} title={me ? `${me.name} — switch person` : 'Choose your name'} {...pop.triggerProps}>
        {me ? <Avatar name={me.name} size={28} /> : <span class="person-switch-empty" aria-hidden="true"><Icon name="user" size={15} /></span>}
        <span class="person-switch-name nav-label">{me ? me.name : 'Choose your name'}</span>
        <Icon name="chevronsUpDown" size={14} class="person-switch-caret nav-label" />
      </button>
      <Popover open={pop.open} onClose={pop.close} anchorRef={pop.anchorRef} placement="bottom-start" width={272} label="Who are you?" role="menu" class="popover-menu">
        <div class="menu-heading">Who are you?</div>
        <MenuList items={items} onClose={pop.close} />
      </Popover>
    </>
  );
}

// TopBar avatar menu: who you are, then (local mode) switch person, People, Settings, Sign out (server sign-in only).
// People stays here because phones have no sidebar. Signed in, it shows on phones only (≥768px the sidebar user card is
// the one account menu).
export function AvatarMenu({ class: cls }) {
  const snap = useStore();
  const { state, index, currentPerson, authRequired, theme } = snap;
  const pop = usePopover();
  const [picking, setPicking] = useState(false);
  const account = accountOf(snap);
  const me = account || (currentPerson ? index?.teamById?.get(currentPerson) : null);
  const close = () => { pop.close(); setPicking(false); };
  const canSignOut = MODE === 'server' && authRequired;
  const items = picking
    ? personItems(state?.team || [], currentPerson)
    : [
      !account && { label: me ? 'Switch person' : 'Choose your name', icon: 'userSwitch', onSelect: () => { setPicking(true); requestAnimationFrame(() => document.querySelector('.avatar-pop [role="menuitemradio"]')?.focus()); }, keepOpen: true },
      !account && { divider: true },
      { label: 'People', icon: 'people', href: href('/people'), class: account ? 'only-mobile' : undefined },
      { label: 'Settings', icon: 'settings', href: href('/settings') },
      { label: theme === 'light' ? 'Dark theme' : 'Light theme', icon: theme === 'light' ? 'moon' : 'sun', onSelect: toggleTheme, class: 'only-mobile' },
      canSignOut && { divider: true },
      canSignOut && signOutItem,
    ].filter(Boolean);
  return (
    <>
      <button ref={pop.anchorRef} type="button" class={cx('avatar-btn', account && 'avatar-btn-phone', cls)} aria-label={me ? `${me.name} — account menu` : 'Account menu'} {...pop.triggerProps}>
        {me ? <Avatar name={me.name} size={28} /> : <span class="person-switch-empty" aria-hidden="true"><Icon name="user" size={15} /></span>}
      </button>
      <Popover open={pop.open} onClose={close} anchorRef={pop.anchorRef} width={248} label="Account" role="menu" class="popover-menu avatar-pop">
        <div class="avatar-menu-head">
          {me ? <Avatar name={me.name} size={32} /> : <span class="person-switch-empty" aria-hidden="true"><Icon name="user" size={15} /></span>}
          <span class="avatar-menu-who">
            <span class="avatar-menu-name">{me ? me.name : 'Not chosen'}</span>
            <span class="avatar-menu-role">{me ? me.role || 'Team member' : 'Pick your name below'}</span>
          </span>
        </div>
        {picking && <div class="menu-heading">Who are you?</div>}
        <MenuList key={picking ? 'pick' : 'main'} items={items} onClose={close} />
      </Popover>
    </>
  );
}
